import React from "react";
import styled from "styled-components";
import { NetworkStatus } from "apollo-client";
import { ONE_MINUTE } from "core/constants";
import { usePageVisibilityPolling } from "core/hooks/usePageVisibilityPolling";
import { useRefetchableGetSchedulerMetricsQuery } from "core/queries/GetSchedulerMetrics";
import { CleanTypeMetrics } from "schedule/components/CleanTypeMetrics";
import { TopLevelMetricsContentLoader } from "schedule/components/TopLevelMetricsContentLoader";
import { TotalMetrics } from "schedule/components/TotalMetrics";
import {
  CleaningRequestCategory,
  FilterStatus,
  TaskScheduleStatusEnum,
} from "typings/globalGQLTypes";

interface TopLevelMetricsContainerProps {
  disabled?: boolean;
}

const TopLevelMetricsContainer = styled.div<TopLevelMetricsContainerProps>`
  display: grid;
  grid-template-columns: minmax(auto, 6fr) minmax(auto, 4fr);
  opacity: ${props => (props.disabled ? "0.5" : "1")};
  pointer-events: ${props => (props.disabled ? "none" : "all")};
  width: 100%;
`;

interface TopLevelMetricsProps {
  assignee: string[] | null | undefined;
  buildingIds: string[] | null | undefined;
  cityId: string | null | undefined;
  cleaningCompanyIds: string[] | null | undefined;
  cleaningRequestCategory: CleaningRequestCategory[] | null | undefined;
  endAt: string;
  sameDayCleansOnly: boolean | null | undefined;
  scheduleStatus: TaskScheduleStatusEnum | undefined;
  startAt: string;
  status: FilterStatus[] | null | undefined;
}

export const TopLevelMetrics = (props: TopLevelMetricsProps) => {
  const {
    assignee,
    buildingIds,
    cityId,
    cleaningCompanyIds,
    cleaningRequestCategory,
    endAt,
    sameDayCleansOnly,
    scheduleStatus,
    startAt,
    status,
  } = props;

  const { data, loading, networkStatus, refetch, startPolling, stopPolling } =
    useRefetchableGetSchedulerMetricsQuery({
      pollInterval: ONE_MINUTE,
      skip: !cityId,
      variables: {
        filters: {
          assignee: assignee || [],
          buildingIds: buildingIds || [],
          city: cityId as string,
          cleaningCompanyIds: cleaningCompanyIds || [],
          cleaningRequestCategory: cleaningRequestCategory || [],
          dueOrLatestEndAt: endAt,
          sameDayCleansOnly,
          scheduledOrEarliestStartAt: startAt,
          scheduleStatus,
          status: status || [],
          taskType: [],
        },
      },
    });

  usePageVisibilityPolling({
    pollInterval: ONE_MINUTE,
    refetch,
    startPolling,
    stopPolling,
  });

  const isPolling = networkStatus === NetworkStatus.poll;
  const isRefetching = networkStatus === NetworkStatus.refetch;
  const isAcuallyLoading = loading && !isPolling && !isRefetching;

  if (!data) return <TopLevelMetricsContentLoader />;

  return (
    <TopLevelMetricsContainer disabled={isAcuallyLoading}>
      <TotalMetrics taskSchedulerMetrics={data.taskSchedulerMetrics} />
      <CleanTypeMetrics
        startAt={startAt}
        taskSchedulerMetrics={data.taskSchedulerMetrics}
      />
    </TopLevelMetricsContainer>
  );
};
